import axios from 'axios';
import JSONbig from 'json-bigint'; // For handling big integers
import store from '@/store';
import { apiConfig } from '@/utils/config';

// Create a custom JSON parser that preserves big integers
const jsonParser = JSONbig({
  useNativeBigInt: true, // Converts big numbers to BigInt (optional)
  alwaysParseAsBig: false, // Only parse numbers that exceed JS safety limit
});

// Create an Axios instance with custom JSON parsing
const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL || apiConfig.baseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  transformResponse: [
    function (data) {
      try {
        // Use json-bigint to parse responses
        return jsonParser.parse(data);
      } catch (e) {
        // Fallback to regular JSON parse if error occurs
        try {
          return JSON.parse(data);
        } catch (e) {
          return data;
        }
      }
    }
  ],
});

// Interceptor to handle retries (unchanged)
apiClient.interceptors.response.use(
  response => response,
  async error => {
    const config = error.config;
    if (!config || !config.retry) return Promise.reject(error);

    config.__retryCount = config.__retryCount || 0;

    if (config.__retryCount >= config.retry) {
      return Promise.reject(error);
    }

    config.__retryCount += 1;
    const delay = new Promise(resolve => setTimeout(resolve, config.retryDelay || 1000));
    await delay;
    return apiClient(config);
  }
);

// Helper function to stringify data with bigint support
const safeStringify = (data) => {
  try {
    return jsonParser.stringify(data);
  } catch (e) {
    return JSON.stringify(data);
  }
};

// Centralized methods for HTTP requests (updated with bigint support)
const apiService = {
  get(endpoint, params = {}, requiresAuth = true, retry = 3, retryDelay = 1000) {
    const config = {
      params,
      retry,
      retryDelay,
      headers: {},
      transformResponse: apiClient.defaults.transformResponse // Ensure our parser is used
    };

    if (requiresAuth) {
      const token = store.getters['auth/currentUser']?.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return apiClient.get(endpoint, config);
  },

  post(endpoint, data, requiresAuth = true, config = {}) {
    config.retry = config.retry || 3;
    config.retryDelay = config.retryDelay || 1000;
    config.transformResponse = apiClient.defaults.transformResponse;

    if (requiresAuth) {
      const token = store.getters['auth/currentUser']?.accessToken;
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }
    }

    // Stringify data with bigint support
    return apiClient.post(endpoint, safeStringify(data), {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'application/json'
      }
    });
  },

  put(endpoint, data, requiresAuth = true, config = {}) {
    config.retry = config.retry || 3;
    config.retryDelay = config.retryDelay || 1000;
    config.transformResponse = apiClient.defaults.transformResponse;

    if (requiresAuth) {
      const token = store.getters['auth/currentUser']?.accessToken;
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
          "Cookie": document.cookie
        };
      }
    }

    return apiClient.put(endpoint, safeStringify(data), {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'application/json'
      }
    });
  },

  delete(endpoint, requiresAuth = true, config = {}) {
    config.retry = config.retry || 3;
    config.retryDelay = config.retryDelay || 1000;
    config.transformResponse = apiClient.defaults.transformResponse;

    if (requiresAuth) {
      const token = store.getters['auth/currentUser']?.accessToken;
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
          "Cookie": document.cookie
        };
      }
    }

    return apiClient.delete(endpoint, config);
  },
};

export default apiService;